import { formatDataValue, formatShippingPriority } from 'utils/formatFns';
import { findEventObject } from '../utils/utils';
import { getEtaStatusChip } from 'pages/VehicleDetails/sectionMappingVehicleInquiry/nationalSoldOrders';
import { getVesselValue } from 'pages/VehicleDetails/sectionMappingVehicleInquiry/transportation';

export const vehicleTrackingFields = {
  // Rail Transport Fields (data?.vehicleTracking?.railTransport?.*)
  actualRailDepart: {
    label: 'Act. Rail Depart',
    getValue: (data) => data?.vehicleTracking?.railTransport?.actualDepartureDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  bolNumber: {
    label: 'BOL Number',
    getValue: (data) => data?.vehicleTracking?.railTransport?.billOfLadingNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedRailArrival: {
    label: 'Est. Rail Arrival',
    getValue: (data) => data?.vehicleTracking?.railTransport?.estimatedArrivalDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedRailDeparture: {
    label: 'Est. Rail Departure',
    getValue: (data) => data?.vehicleTracking?.railTransport?.estimatedDepartureDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  legOriginToDestination: {
    label: 'Leg Origin "to" Leg Destination',
    getValue: (data) => ({
      origin: data?.vehicleTracking?.railTransport?.shipmentLegOrigin,
      destination: data?.vehicleTracking?.railTransport?.shipmentLegDestination,
    }),
    formatValue: ({ origin, destination }) => {
      const formattedOrigin = formatDataValue({ value: origin });
      const formattedDestination = formatDataValue({ value: destination });
      return `${formattedOrigin} - ${formattedDestination}`;
    },
  },
  railCarrier: {
    label: 'Rail Carrier',
    getValue: (data) => data?.vehicleTracking?.railTransport?.railCarrier,
    formatValue: (value) => formatDataValue({ value }),
  },
  railcarPrefix: {
    label: 'Railcar Prefix',
    getValue: (data) => data?.vehicleTracking?.railTransport?.railcarPrefix,
    formatValue: (value) => formatDataValue({ value }),
  },
  railcarNumber: {
    label: 'Railcar Number',
    getValue: (data) => data?.vehicleTracking?.railTransport?.railcarCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  railcarPosition: {
    label: 'Railcar Position',
    getValue: (data) => data?.vehicleTracking?.railTransport?.railcarPosition,
    formatValue: (value) => formatDataValue({ value }),
  },
  railOrigin: {
    label: 'Rail Origin',
    getValue: (data) => data?.vehicleTracking?.railTransport?.shipmentLegOrigin,
    formatValue: (value) => formatDataValue({ value }),
  },

  // Truck Transport Fields (data?.vehicleTracking?.truckTransport?.*)
  actualTruckArrive: {
    label: 'Act. Truck Arrive',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.actualArrivalDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  actualTruckDepart: {
    label: 'Act. Truck Depart',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.actualDepartureDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedTruckArrival: {
    label: 'Est. Truck Arrival',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.estimatedArrivalDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedTruckDepart: {
    label: 'Est. Truck Departure',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.estimatedDepartureDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  portPickup: {
    label: 'Port Pickup',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.portPickupIndicatorFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  preloadDate: {
    label: 'Pre-Load Date',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.preLoadDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  tenderDate: {
    label: 'Tender Date',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.tenderedDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  truckDestination: {
    label: 'Truck Destination',
    getValue: (_, transportationData) =>
      findEventObject(transportationData, 'departure', 'On Truck for Delivery')?.destination,
    formatValue: (value) => formatDataValue({ value }),
  },
  truckLoadNumber: {
    label: 'Truck Load Number',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.loadNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  truckOrigin: {
    label: 'Truck Origin',
    getValue: (data) => data?.vehicleTracking?.truckTransport?.shipmentLegOrigin,
    formatValue: (value) => formatDataValue({ value }),
  },

  // Vessel Transport Fields (data?.vehicleTracking?.vesselTransport?.*)
  actualVesselArrive: {
    label: 'Act. Vessel Arrive',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.actualVesselArrivalDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  actualVesselDepart: {
    label: 'Act. Vessel Depart',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.assignedLoadingActualTimestampFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  dischargePort: {
    label: 'Discharge Port',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.exportVehicleDischargePort,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedVesselArrival: {
    label: 'Est. Vessel Arrival',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.estimatedVesselArrivalDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  estimatedVesselDepart: {
    label: 'Est. Vessel Departure',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.estimatedVesselDepartureDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  finalLoadDate: {
    label: 'Final Load Date',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.finalLoadDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  finalVoyageNumber: {
    label: 'Final Voyage No.',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.finalVoyageNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  shippingCompany: {
    label: 'Shipping Company',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.shippingCompanyName,
    formatValue: (value) => formatDataValue({ value }),
  },
  shipperName: {
    label: 'Shipper Name',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.shipperName,
    formatValue: (value) => formatDataValue({ value }),
  },
  shippingPriority: {
    label: 'Shipping Priority',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.shippingPriority,
    formatValue: (value) => (value ? formatShippingPriority(value) : null),
  },
  tmcVesselNumber: {
    label: 'TMC Vessel No.',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.vesselNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  tvaDate: {
    label: 'TVA Date',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.tvaDateFmt,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselCaseNumber: {
    label: 'Vessel Case Number',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.caseNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselCode: {
    label: 'Vessel Code',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.vesselCode,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselDestination: {
    label: 'Vessel Destination',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.unloadPortName,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselLoadingPort: {
    label: 'Vessel Loading Port',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.exportVehicleLoadingPort,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselName: {
    label: 'Vessel Name',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.vesselName,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselOrigin: {
    label: 'Vessel Origin',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.departurePortName,
    formatValue: (value) => formatDataValue({ value }),
  },
  vesselYearNumber: {
    label: 'Vessel Year | Number',
    getValue: (data) => getVesselValue({ vehicle: data }),
    formatValue: (value) => formatDataValue({ value }),
  },
  voyageNumber: {
    label: 'Voyage No.',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.voyageNumber,
    formatValue: (value) => formatDataValue({ value }),
  },
  yardLocation: {
    label: 'Yard Location',
    getValue: (data) => data?.vehicleTracking?.vesselTransport?.yardLocation,
    formatValue: (value) => formatDataValue({ value }),
  },
  //Other Vehicle Tracking Fields (data?.vehicleTracking?.*)
  customerEtaStatus: {
    label: 'Customer ETA Status',
    getValue: (data) => data?.vehicleTracking?.swCustomerEtaStatus,
    formatValue: (value, options = {}) => {
      return value ? getEtaStatusChip(value, options.isPrint) : null;
    },
  },
  customerInitialETAWindow: {
    label: 'Customer Initial ETA Window',
    getValue: (data) => ({
      windowStart: data?.vehicleTracking?.customerOrgEtaWindowStartFmt,
      windowEnd: data?.vehicleTracking?.customerOrgEtaWindowEndFmt,
    }),
    formatValue: ({ windowStart, windowEnd }) => {
      const formattedStart = formatDataValue({ value: windowStart });
      const formattedEnd = formatDataValue({ value: windowEnd });
      return `${formattedStart} - ${formattedEnd}`;
    },
  },
};
